import {
  ControlledDateInput,
  ControlledInput,
} from "components/Card/ControlledInput";
import { MaskedInput, TextInput, ControlledTextArea } from "components/inputs";
import type {
  Control,
  UseFormGetValues,
  UseFormSetValue,
} from "react-hook-form";
import type { ConstantTypes } from "types/DataTypes";

import type { LienFormValues } from "../../../LienTypes";

type FinalLienRequestSectionProps = {
  control: Control<LienFormValues>;
  getValues: UseFormGetValues<LienFormValues>;
  setValue: UseFormSetValue<LienFormValues>;
  handleFieldChange: (field: string | null, value: unknown) => Promise<void>;
  constants?: ConstantTypes;
};

const FinalLienRequestSection = ({
  control,
  getValues,
  setValue,
  handleFieldChange,
}: FinalLienRequestSectionProps) => {
  const submittedOn = getValues("final_lien_request_submitted_on");
  const shouldDisableFields =
    getValues("initial_lien_request_no_interest_letter_received") ||
    getValues("initial_lien_request_itemization_received");

  return (
    <>
      <h3>Final Notice</h3>
      <ControlledDateInput
        control={control}
        name="final_lien_request_submitted_on"
        label="Date Request Submitted"
        onSave={async (value) => {
          if (!value) {
            // Clear all fields when date is removed
            setValue("final_lien_request_user_notes", null);
            setValue("final_lien_request_plan_name", null);
            setValue("final_lien_request_plan_address", null);
            setValue("final_lien_request_plan_contact_number", null);
            setValue("final_lien_request_subrogation_agent_name", null);
            setValue("final_lien_request_subrogation_phone", null);
            setValue("final_lien_request_notice_of_settlement_date", null);
            setValue("final_lien_request_tracking_number", null);
            setValue("final_lien_request_method", null);
            await handleFieldChange(null, {
              final_lien_request_submitted_on: null,
              final_lien_request_user_notes: null,
              final_lien_request_plan_name: null,
              final_lien_request_plan_address: null,
              final_lien_request_plan_contact_number: null,
              final_lien_request_subrogation_agent_name: null,
              final_lien_request_subrogation_phone: null,
              final_lien_request_notice_of_settlement_date: null,
              final_lien_request_tracking_number: null,
              final_lien_request_method: null,
            });
          } else {
            await handleFieldChange("final_lien_request_submitted_on", value);
          }
        }}
        errors={{}}
        readOnly={shouldDisableFields}
      />
      {submittedOn && (
        <>
          <ControlledInput
            control={control}
            name="final_lien_request_tracking_number"
            label="Request Tracking Number"
            onSave={(value) =>
              handleFieldChange("final_lien_request_tracking_number", value)
            }
            errors={{}}
            componentType={TextInput}
            readOnly={shouldDisableFields}
          />
          <ControlledInput
            control={control}
            name="final_lien_request_plan_name"
            label="Plan Name"
            onSave={(value) =>
              handleFieldChange("final_lien_request_plan_name", value)
            }
            errors={{}}
            componentType={TextInput}
            readOnly={shouldDisableFields}
          />
          <ControlledInput
            control={control}
            name="final_lien_request_plan_address"
            label="Plan Address"
            onSave={(value) =>
              handleFieldChange("final_lien_request_plan_address", value)
            }
            errors={{}}
            componentType={TextInput}
            readOnly={shouldDisableFields}
          />
          <ControlledInput
            control={control}
            name="final_lien_request_plan_contact_number"
            label="Plan Contact Number"
            onSave={(value) =>
              handleFieldChange("final_lien_request_plan_contact_number", value)
            }
            errors={{}}
            componentType={MaskedInput}
            mask="phone"
            readOnly={shouldDisableFields}
          />
          <ControlledInput
            control={control}
            name="final_lien_request_subrogation_agent_name"
            label="Subrogation Agent Name"
            onSave={(value) =>
              handleFieldChange(
                "final_lien_request_subrogation_agent_name",
                value,
              )
            }
            errors={{}}
            componentType={TextInput}
            readOnly={shouldDisableFields}
          />
          <ControlledInput
            control={control}
            name="final_lien_request_subrogation_phone"
            label="Subrogation Phone"
            onSave={(value) =>
              handleFieldChange("final_lien_request_subrogation_phone", value)
            }
            errors={{}}
            componentType={MaskedInput}
            mask="phone"
            readOnly={shouldDisableFields}
          />
          <ControlledDateInput
            control={control}
            name="final_lien_request_notice_of_settlement_date"
            label="Notice of Settlement Date"
            onSave={(value) =>
              handleFieldChange(
                "final_lien_request_notice_of_settlement_date",
                value,
              )
            }
            errors={{}}
            readOnly={shouldDisableFields}
          />
          <ControlledInput
            control={control}
            name="final_lien_request_method"
            label="Request Method"
            onSave={(value) =>
              handleFieldChange("final_lien_request_method", value)
            }
            errors={{}}
            componentType={TextInput}
            readOnly={shouldDisableFields}
          />

          <div>
            <label>Notes:</label>
            <ControlledTextArea
              control={control}
              name="final_lien_request_user_notes"
              label={null}
              advancedEdit={true}
              maxLength={2000}
              errors={{}}
              onSave={(value) =>
                handleFieldChange("final_lien_request_user_notes", value)
              }
            />
          </div>
        </>
      )}
    </>
  );
};

export default FinalLienRequestSection;
